import React, {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'tolgee';

import {useCreateAdvancedConfigMutation} from '~api/models.mutations';
import {
  useCurrentModelAdvancedConfigurationQuery,
  useCurrentModelTrainingConfigurationQuery,
  usePreprocessingRoisQuery,
} from '~api/models.queries';
import {useIsAdmin, useIsTrainer} from '~hooks/misc.hooks';
import {selectCurrentModelId} from '~redux/reducers/annotatorReducer';
import {useAppSelector} from '~redux/index';

import {showNotification} from '~components/common/Notification';
import {selectRoiScopesForImage, toRoiScope} from '../chips/utils';

export interface DefineRoiContextValue {
  isRoiDrawingMode: boolean;
  setIsRoiDrawingMode: (isDrawing: boolean) => void;
  onToggleRoi: () => void;
  openDefineROIDialog: boolean;
  handleRoiDialogOpen: () => void;
  handleRoiDialogClose: () => void;
  handleRoiDialogCancel: () => void;
  handleSetIsRoiEditMode: (isEditMode: boolean) => void;
  handleDeleteRoi: (name: string) => Promise<void>;
  handleRoiDialogSave: (definedRoi: DefinedRoi) => void;
  definedRoi: DefinedRoi | null;
  trainingConfig?: ModelTrainingConfigurationResponse | undefined;
  handleSetDefinedRoi: (roi: DefinedRoi | null) => void;
  isRoiEditMode: boolean;
  editingRoiOriginalName: string | null;
  handleSetEditingRoiOriginalName: (name: string | null) => void;
  handleSaveDrawnROI: (coordinates: Coordinate[]) => Promise<void>;
  getCurrentImageRois: (args: {perspective: number | null; productType: string | null}) => RoiSchema[];
  roisWithMetadata: Record<string, RoiWithMetadata>;
  canEditRois: boolean;
}

export const DefineRoiContext = createContext<DefineRoiContextValue | undefined>(undefined);

export const useDefineRoi = () => {
  const context = useContext(DefineRoiContext);
  if (context === undefined) {
    throw new Error('useDefineRoi must be used within a DefineRoiProvider');
  }
  return context;
};

const mergeRoiScope = <T,>(existing: T[] | null, incoming: T[] | null): T[] | null => {
  if (existing === null || incoming === null) return null;
  return [...new Set([...existing, ...incoming])];
};

export const DefineRoiProvider = (props: {children: ReactNode}) => {
  const [openDefineROIDialog, setOpenDefineROIDialog] = useState(false);
  const [isRoiDrawingMode, setIsRoiDrawingMode] = useState(false);
  const [isRoiEditMode, setIsRoiEditMode] = useState(false);
  const [editingRoiOriginalName, setEditingRoiOriginalName] = useState<string | null>(null);
  const {t} = useTranslation('error');
  const isAdmin = useIsAdmin({validateEmail: false});
  const isTrainer = useIsTrainer();
  const hasRoiEditPermission = isAdmin || isTrainer;
  const currentModelId = useAppSelector(selectCurrentModelId);
  const preprocessingRoisQuery = usePreprocessingRoisQuery(currentModelId);

  const getCurrentImageRois = useCallback(
    ({perspective, productType}: {perspective: number | null; productType: string | null}) => {
      const entries = preprocessingRoisQuery.data?.preprocessingRois ?? [];
      return selectRoiScopesForImage(entries, toRoiScope, {perspective, productType}).flatMap((entry) => entry.rois);
    },
    [preprocessingRoisQuery.data],
  );

  // Group roisWithMetadata by preprocessingName
  const roisWithMetadata: Record<string, RoiWithMetadata> = useMemo(() => {
    const rois = {} as Record<string, RoiWithMetadata>;
    for (const entry of preprocessingRoisQuery.data?.preprocessingRois || []) {
      for (const roi of entry.rois) {
        const key = roi.preprocessingName;
        const coordinates = roi.coordinates;
        const scope = toRoiScope(entry);
        const perspectives = scope.perspective !== null ? [scope.perspective] : null;
        const productTypes = scope.productType !== null ? [scope.productType] : null;
        if (!rois[key]) {
          rois[key] = {
            name: key,
            coordinates,
            perspectives,
            productTypes,
            scopes: [scope],
          };
        } else {
          rois[key] = {
            name: key,
            coordinates,
            perspectives: mergeRoiScope(rois[key].perspectives, perspectives),
            productTypes: mergeRoiScope(rois[key].productTypes, productTypes),
            scopes: [...rois[key].scopes, scope],
          };
        }
      }
    }

    return rois;
  }, [preprocessingRoisQuery.data]);

  const currentModelAdvancedConfigurationQuery = useCurrentModelAdvancedConfigurationQuery(
    hasRoiEditPermission,
    currentModelId,
  );
  const currentModelTrainingConfigurationQuery = useCurrentModelTrainingConfigurationQuery(isAdmin, currentModelId);

  const currentModelAdvancedConfiguration = currentModelAdvancedConfigurationQuery.data;
  const currentModelTrainingConfiguration = currentModelTrainingConfigurationQuery.data;

  const canEditRois = hasRoiEditPermission && currentModelAdvancedConfiguration !== undefined;

  const {mutate: createAdvancedConfig} = useCreateAdvancedConfigMutation({
    onSuccess: () => {
      setIsRoiDrawingMode(false);
      setIsRoiEditMode(false);
      setDefinedRoi(null);
      setEditingRoiOriginalName(null);
      currentModelAdvancedConfigurationQuery.refetch();
      preprocessingRoisQuery.refetch();
    },
    onError: () => {
      setIsRoiDrawingMode(false);
      setIsRoiEditMode(false);
      setDefinedRoi(null);
      setEditingRoiOriginalName(null);
    },
  });

  const [definedRoi, setDefinedRoi] = useState<DefinedRoi | null>(null);
  const [trainingConfig, setTrainingConfig] = useState(currentModelTrainingConfiguration);

  useEffect(() => {
    setTrainingConfig(currentModelTrainingConfiguration);
  }, [currentModelTrainingConfiguration]);

  const onToggleRoi = useCallback(() => {
    setIsRoiDrawingMode(!isRoiDrawingMode);
  }, [isRoiDrawingMode]);

  const handleRoiDialogOpen = useCallback(() => {
    setOpenDefineROIDialog(true);
  }, []);

  const handleRoiDialogClose = useCallback(() => {
    setOpenDefineROIDialog(false);
  }, []);

  const handleRoiDialogCancel = useCallback(() => {
    setOpenDefineROIDialog(false);
    setIsRoiDrawingMode(false);
    setIsRoiEditMode(false);
    setEditingRoiOriginalName(null);
  }, []);

  const handleSetIsRoiEditMode = useCallback((isEditMode: boolean) => {
    setIsRoiEditMode(isEditMode);
    setIsRoiDrawingMode(isEditMode);
    if (!isEditMode) {
      setEditingRoiOriginalName(null);
    }
  }, []);

  const handleDeleteRoi = useCallback(
    async (name: string) => {
      if (currentModelAdvancedConfiguration === undefined) {
        showNotification({message: t('genericError'), severity: 'error'});
        return;
      }

      const {[name]: _, ...rest} = currentModelAdvancedConfiguration.preprocessing || {};
      if (!rest) return;

      const newAdvancedConfig = {
        ...currentModelAdvancedConfiguration,
        preprocessing: {
          ...rest,
        },
      };

      delete newAdvancedConfig.warning_messages;

      await createAdvancedConfig({newAdvancedConfig});
      setDefinedRoi(null);
    },
    [createAdvancedConfig, currentModelAdvancedConfiguration, t],
  );

  const handleSetDefinedRoi = useCallback((roi: DefinedRoi | null) => {
    setDefinedRoi(roi);
    if (roi === null) {
      setEditingRoiOriginalName(null);
    }
  }, []);

  const handleSetEditingRoiOriginalName = useCallback((name: string | null) => {
    setEditingRoiOriginalName(name);
  }, []);

  const handleSaveDrawnROI = useCallback(
    async (coordinates: Coordinate[]) => {
      setDefinedRoi({coordinates, name: '', perspectives: [], productTypes: []});
      handleRoiDialogOpen();
    },
    [setDefinedRoi, handleRoiDialogOpen],
  );

  const handleRoiDialogSave = useCallback(
    async (definedRoi: DefinedRoi) => {
      if (definedRoi === null) return;
      if (currentModelAdvancedConfiguration === undefined) {
        showNotification({message: t('genericError'), severity: 'error'});
        return;
      }

      const existingPreprocessing = {
        ...(currentModelAdvancedConfiguration.preprocessing || {}),
      };

      if (isRoiEditMode && editingRoiOriginalName) {
        delete existingPreprocessing[editingRoiOriginalName];
      }

      const bounds = definedRoi.coordinates.reduce(
        (acc, point) => ({
          x_min: Math.min(acc.x_min, point.x),
          x_max: Math.max(acc.x_max, point.x),
          y_min: Math.min(acc.y_min, point.y),
          y_max: Math.max(acc.y_max, point.y),
        }),
        {
          x_min: Infinity,
          x_max: -Infinity,
          y_min: Infinity,
          y_max: -Infinity,
        },
      );
      const newAdvancedConfig = {
        ...currentModelAdvancedConfiguration,
        preprocessing: {
          ...existingPreprocessing,
          [definedRoi.name]: {
            ...(definedRoi.perspectives.length > 0 && {apply_to_perspectives: definedRoi.perspectives}),
            ...(definedRoi.productTypes.length > 0 && {apply_to_product_types: definedRoi.productTypes}),
            modules: [
              {
                name: 'maddox_ml_transforms.preprocessing.invertible.ROIPreprocessing',
                args: {
                  always_apply: true,
                  p: 1,
                  ...bounds,
                },
              },
            ],
            priority: 1,
          },
        },
      };

      delete newAdvancedConfig.warning_messages;

      await createAdvancedConfig({newAdvancedConfig});
      setIsRoiDrawingMode(false);
      setIsRoiEditMode(false);
      setDefinedRoi(null);
    },
    [createAdvancedConfig, currentModelAdvancedConfiguration, editingRoiOriginalName, isRoiEditMode, t],
  );

  const value = useMemo(
    () => ({
      isRoiDrawingMode,
      setIsRoiDrawingMode,
      onToggleRoi,
      openDefineROIDialog,
      handleRoiDialogOpen,
      handleRoiDialogClose,
      handleRoiDialogCancel,
      handleRoiDialogSave,
      definedRoi,
      trainingConfig,
      handleSetDefinedRoi,
      handleSaveDrawnROI,
      editingRoiOriginalName,
      handleSetEditingRoiOriginalName,
      handleSetIsRoiEditMode,
      isRoiEditMode,
      handleDeleteRoi,
      getCurrentImageRois,
      roisWithMetadata,
      canEditRois,
    }),
    [
      isRoiDrawingMode,
      onToggleRoi,
      openDefineROIDialog,
      handleRoiDialogOpen,
      handleRoiDialogClose,
      handleRoiDialogCancel,
      handleRoiDialogSave,
      definedRoi,
      trainingConfig,
      handleSetDefinedRoi,
      handleSaveDrawnROI,
      handleSetIsRoiEditMode,
      editingRoiOriginalName,
      handleSetEditingRoiOriginalName,
      isRoiEditMode,
      handleDeleteRoi,
      getCurrentImageRois,
      roisWithMetadata,
      canEditRois,
    ],
  );

  return <DefineRoiContext.Provider value={value} {...props} />;
};
